import { useState, useEffect } from 'react';
import {
  Renew,
  CloudUpload,
  CheckmarkOutline,
  Checkmark,
} from '@carbon/icons-react';
import { useRevalidator } from 'react-router';

interface DeploymentOverlayProps {
  isEnabled: boolean;
}

export function DeploymentOverlay({ isEnabled }: DeploymentOverlayProps) {
  const revalidator = useRevalidator();
  const [status, setStatus] = useState<'deploying' | 'success' | 'hidden'>(
    isEnabled ? 'deploying' : 'hidden',
  );
  const [dots, setDots] = useState('');

  // Poll for deployment mode changes every 5 seconds
  useEffect(() => {
    if (status === 'hidden') return;

    const interval = setInterval(() => {
      revalidator.revalidate();
    }, 5000);

    return () => clearInterval(interval);
  }, [status, revalidator]);

  // Handle transitions based on isEnabled prop
  useEffect(() => {
    if (isEnabled && status === 'hidden') {
      // Deployment mode just turned on
      setStatus('deploying');
    } else if (!isEnabled && status === 'deploying') {
      // Deployment mode just turned off - show success state
      setStatus('success');
      // Show success message for 3 seconds, then reload the page
      setTimeout(() => {
        window.location.reload();
      }, 3000);
    }
  }, [isEnabled, status]);

  // Animate loading dots
  useEffect(() => {
    if (status !== 'deploying') return;
    const interval = setInterval(() => {
      setDots((prev) => (prev.length >= 3 ? '' : prev + '.'));
    }, 500);
    return () => clearInterval(interval);
  }, [status]);

  if (status === 'hidden') return null;

  const isSuccess = status === 'success';

  return (
    <div className="fixed inset-0 z-99999 bg-background/80 backdrop-blur-xs text-foreground flex items-center justify-center p-6 transition-all duration-700">
      {/* Subtle Radial Background Glow (Turns green on success) */}
      <div className="absolute inset-0 flex items-center justify-center transition-colors duration-1000">
        <div
          className={`w-125 h-125 rounded-full blur-[120px] ${isSuccess ? 'bg-green-500/10' : 'bg-primary-base/5'}`}
        />
      </div>

      {/* Main Content Card */}
      <div className="bg-card border border-border shadow-2xl w-full max-w-xl p-12 text-center relative z-10 flex flex-col items-center gap-10">
        {/* Branded Icon Header */}
        <div className="relative">
          <div
            className={`p-5 rounded-full border shadow-inner transition-colors duration-700 ${isSuccess ? 'bg-green-500/10 border-green-500/30' : 'bg-muted/5 border-border'}`}
          >
            {isSuccess ? (
              <CheckmarkOutline
                size={32}
                className="text-green-500 transition-opacity duration-500"
              />
            ) : (
              <CloudUpload
                size={32}
                className="text-muted-foreground transition-opacity duration-500"
              />
            )}
          </div>
          {/* Pulsating Orbit Icon (Hides on success) */}
          {!isSuccess && (
            <div className="absolute top-1 right-1 transition-opacity duration-300">
              <span className="flex h-4 w-4 relative">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary-base opacity-75"></span>
                <span className="relative inline-flex h-4 w-4 rounded-full bg-primary-base border-2 border-[#09090b]"></span>
              </span>
            </div>
          )}
        </div>

        {/* Title & Reassuring Message */}
        <div className="space-y-4">
          <h1 className="text-3xl font-black tracking-tight text-foreground leading-none transition-all duration-500">
            {isSuccess ? 'Update Complete' : 'System Update in Progress'}
          </h1>
          <div className="h-12 flex items-start justify-center">
            <p className="text-sm md:text-base text-muted-foreground leading-relaxed max-w-md mx-auto transition-opacity duration-500">
              {isSuccess
                ? "Thank you for your patience! The platform is up to date and you're good to go."
                : "We're deploying a quick update. This won't take long—we'll be right back online shortly."}
            </p>
          </div>
        </div>

        {/* AESTHETIC LOADING VISUALIZERS */}
        <div className="flex items-center gap-2">
          {[0, 1, 2, 3].map((node) => (
            <div key={node} className="flex flex-col items-center gap-3">
              {/* Node Box */}
              <div
                className={`w-16 h-12 rounded-sm border flex items-center justify-center shadow-inner transition-colors duration-700 ${isSuccess ? 'bg-green-500/5 border-green-500/20' : 'bg-muted/5 border-border animate-pulse'}`}
                style={{
                  animationDelay: isSuccess ? '0ms' : `${node * 200}ms`,
                  animationDuration: '2s',
                }}
              >
                {isSuccess ? (
                  <Checkmark
                    size={20}
                    className="text-green-500 transition-opacity duration-500"
                  />
                ) : (
                  <Renew
                    size={20}
                    className="text-muted-foreground/50 animate-spin transition-opacity duration-500"
                    style={{ animationDuration: '3s' }}
                  />
                )}
              </div>

              {/* Status Indicator Bar */}
              <div
                className={`w-16 h-1 rounded-sm transition-colors duration-700 ${isSuccess ? 'bg-green-500' : 'bg-primary-base/50 animate-pulse'}`}
                style={{
                  animationDelay: isSuccess ? '0ms' : `${node * 200}ms`,
                  animationDuration: '2s',
                }}
              />
            </div>
          ))}
        </div>

        {/* Generic Status Text */}
        <div className="w-full pt-8 border-t border-border mt-2">
          <p className="text-xs font-bold text-muted-foreground uppercase tracking-widest mb-1.5">
            Current Status
          </p>
          <p
            className={`text-lg font-mono font-bold tracking-tight flex items-center gap-2 justify-center transition-colors duration-700 ${isSuccess ? 'text-green-500' : 'text-primary-base'}`}
          >
            {isSuccess ? 'System Online' : `Applying Updates${dots}`}
          </p>
          <p className="text-xs text-muted-foreground/80 mt-1.5 italic transition-opacity duration-500">
            {isSuccess
              ? 'Returning to workspace...'
              : 'The platform will automatically reload when complete.'}
          </p>
        </div>

        {/* Brand Identifier */}
        <div className="absolute bottom-4 left-4 right-4 flex justify-between items-center text-[10px] text-muted-foreground/50 font-medium">
          <span className="font-bold">CTRLSPIRE DEV TECHNOLOGIES</span>
          <span className="font-bold">GLOBAL DEPLOYMENT LOCK</span>
        </div>
      </div>
    </div>
  );
}
